import { Stack, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { IconRefresh } from '@tabler/icons-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import RootButton from '@/components/Buttons/RootButton'
import { Amount } from '@/components/CurrencyInput'
import CastVotesModal from '@/components/Modals/Governance/CastVotesModal'
import ModalSkeleton from '@/components/Modals/ModalSkeleton'
import { useElectionsContext } from '@/contexts/ElectionsContext'
import { useGovernanceContext } from '@/contexts/GovernanceContext'
import { useAuth } from '@/plugins/auth'

import VotingAction from '../GovernanceList/ListActions/VotingAction'
import NomineesList from '../GovernanceList/NomineesList'

interface VotingProps {}

const Voting: React.FC<VotingProps> = () => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const { address } = useAuth()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const [opened, { open, close }] = useDisclosure()

  // Governance data
  const { nominationData, electionData, vote } = useGovernanceContext()
  const [votes, setVotes] = useState<{ [key: string]: Amount }>({})
  const { usedVoteBalance, remainingVoteBalance, isLoading, refetchData } = useElectionsContext()

  // Is voting period active
  const votingActive = useMemo(() => {
    const now = new Date().getTime() / 1000
    return electionData.votingTime < now && electionData.endTime > now
  }, [electionData])

  const [countdown, setCountdown] = useState(60)

  // Vote logic
  const handleVoteChange = (nominee: string, value: Amount) => {
    if (Number(value.value) === 0) {
      setVotes((prevVotes: { [key: string]: Amount }) => {
        const newVotes = { ...prevVotes }
        delete newVotes[nominee]
        return newVotes
      })
    } else {
      setVotes((prevVotes: { [key: string]: Amount }) => ({
        ...prevVotes,
        [nominee]: value,
      }))
    }
  }

  // Handle manual refresh
  const handleRefresh = () => {
    refetchData()
    setCountdown(60)
  }

  // Refetch logic with countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          refetchData()
          return 60
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  return (
    <>
      <NomineesList
        title={t('components.governance.voting.title', 'Voting')}
        candidates={nominationData.filter(
          cand => cand.status === 'ACCEPTED' || cand.status === 'REMOVED'
        )}
        phase="voting"
        button={
          <Stack style={{ flexDirection: 'row', gap: '15px', alignItems: 'center' }}>
            {address && (
              <>
                {!isMobile && (
                  <>
                    <Title order={5} style={{ color: theme.colors.paragraph[0] }}>
                      Balance: {vote.fullDisplay} VOTE
                    </Title>

                    <Title order={5} style={{ color: theme.colors.paragraph[0] }}>
                      Used: {isLoading ? 'N/A' : usedVoteBalance.fullDisplay} VOTE
                    </Title>
                  </>
                )}

                <Title order={5} style={{ color: theme.colors.paragraph[0] }}>
                  Remaining: {isLoading ? 'N/A' : remainingVoteBalance.fullDisplay} VOTE
                </Title>

                <RootButton
                  style1={false}
                  onClick={open}
                  disabled={
                    // if there is no votes or if the votes that exist are with value 0
                    !Object.values(votes).some(v => Number(v.value) > 0) ||
                    Object.values(votes).every(v => Number(v.value) === 0) ||
                    Object.values(votes).some(v => v.error) ||
                    !votingActive ||
                    !address
                  }
                >
                  {t('buttons.vote', 'Vote')}
                </RootButton>
              </>
            )}
            <Stack
              style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Stack
                style={{
                  fontSize: '14px',
                  color: theme.colors.paragraph[0],
                  minWidth: '30px',
                }}
              >
                {countdown}s
              </Stack>
              <RootButton
                style1={false}
                onClick={handleRefresh}
                style={{
                  padding: '8px',
                }}
              >
                <IconRefresh size={20} />
              </RootButton>
            </Stack>
          </Stack>
        }
        action={VotingAction}
        actionProps={{
          votes,
          handleVoteChange,
          remainingVoteBalance,
          nominee: '',
          status: 'ACCEPTED',
          isBreakpoint: false,
          votingActive,
        }}
      />
      <ModalSkeleton title="Vote" opened={opened} onClose={close}>
        <CastVotesModal isMobile={isMobile} votes={votes} close={close} setVotes={setVotes} />
      </ModalSkeleton>
    </>
  )
}

export default Voting
